import type { ReactNode } from "react";
import Logo from "~/design/ui/Logo";
import cn from "~/utils/cn";

export type StatusTone = "success" | "danger" | "warning";
const tones: Record<StatusTone, string> = {
  success: "bg-success-soft text-success",
  danger: "bg-danger-soft text-danger",
  warning: "bg-warning-soft text-warning",
};

export function StatusIcon({ tone, children, className }: { tone: StatusTone; children: ReactNode; className?: string }) {
  return <span className={cn("grid size-24 place-items-center rounded-full [&_svg]:size-11", tones[tone], className)}>{children}</span>;
}

// Full-height centered status page (errors, payment result). Also renders inside the root ErrorBoundary document.
export default function StatusScreen({
  icon,
  title,
  description,
  action,
  footer,
  children,
}: {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-[100dvh] w-full flex-col px-[var(--gutter)] pb-[calc(env(safe-area-inset-bottom)+2rem)] pt-[env(safe-area-inset-top)]">
      <header className="mx-auto flex h-16 w-full max-w-5xl items-center">
        <Logo />
      </header>
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center py-10 text-center">
        {icon}
        <h1 className="mt-8 font-display text-[1.75rem] font-black leading-[1.4] text-ink">{title}</h1>
        {description && <p className="mt-3 max-w-[34ch] text-[15px] leading-7 text-ink-2">{description}</p>}
        {action && <div className="mt-8 w-full max-w-xs">{action}</div>}
        {footer}
        {children}
      </main>
    </div>
  );
}
